import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Appointment } from '../../services/configApi/activitySheet/weeklyActivitySheetProvider';
import { Box, TableCell, TableRow, Tooltip } from '@mui/material';
import MediumTypography from '../../components/formlib/MediumTypography';
import { LOCAL_TIME_12_HOUR_FORMAT } from '../../services/Constant';
import { getAppStartTime } from '../../utils/dateUtil';
import { formatName } from '../../utils/nameUtils';
import { formatNumberToDecimal, getStatusColor } from '../../utils/utilities';
import { ReactComponent as SingleIcon } from '../../assets/images/singleIcon.svg';
import { ReactComponent as AcceptIcon } from '../../assets/images/acceptIcon.svg';
import { ReactComponent as RejectIcon } from '../../assets/images/rejectIcon.svg';
import { ReactComponent as UndoIcon } from '../../assets/images/undoIcon.svg';
import { ReactComponent as StatusIcon } from '../../assets/images/statusIcon.svg';
import TextWithIcon from '../../components/formlib/TextWithIcon';
import { useIntl } from 'react-intl';
import { LookUpContext } from '../../context/LookUpContextProvider';
import { LookupItem } from '../../services/configApi/tcsLookups';
import { useNavigate } from 'react-router-dom';
import { StatusType } from './ActivityTableRow';
import { ActivitySheetContext } from '../../context/ActivitySheetContext';
import VisitActionButtons from './components/VisitActionButtons';
import GroupBillingHeader from './components/GroupBillingHeader';

export interface VisitNote {
  progressNotesId: string;
  visitId: string;
  note: string;
}

interface ActivitySheetVisitViewProps {
  activitySheetId: string;
  appointment: Appointment;
  isGroupBilling: boolean;
  index: number;
  removeBorder: boolean;
  hasInterpreter: boolean;
  parentArchived: boolean;
  rowId: number;
  parentStatus: StatusType | null;
  selectedDate?: Date;
  onUpdateVisitNote: (note: VisitNote) => void;
}

const ActivitySheetVisitView: FC<ActivitySheetVisitViewProps> = ({
  activitySheetId,
  appointment,
  isGroupBilling,
  index,
  removeBorder,
  hasInterpreter,
  parentArchived,
  rowId,
  parentStatus,
  selectedDate,
  onUpdateVisitNote,
}) => {
  const rctl = useIntl();
  const navigate = useNavigate();
  const { lookups } = useContext(LookUpContext);
  const { context, canBillingHoursEdit } = useContext(ActivitySheetContext);
  const [serviceCode, setServiceCode] = useState<LookupItem[]>([]);
  const [settingCode, setSettingCode] = useState<LookupItem[]>([]);
  const [clientLocations, setClientLocations] = useState<LookupItem[]>([]);

  useEffect(() => {
    if (lookups === null) return;

    setServiceCode(lookups.serviceCode);
    setSettingCode(lookups.settingsCode);
    setClientLocations(lookups.location);
  }, [lookups]);

  const handleApprove = useCallback(() => {
    if (context?.updateVisitStatus && appointment.visitId) {
      context.updateVisitStatus(
        activitySheetId,
        appointment.visitId,
        'Approved',
        true,
      );
    }
  }, [context, activitySheetId, appointment.visitId]);

  const handleReject = useCallback(() => {
    if (context?.updateVisitStatus && appointment.visitId) {
      context.updateVisitStatus(
        activitySheetId,
        appointment.visitId,
        'Rejected',
        true,
      );
    }
  }, [context, activitySheetId, appointment.visitId]);

  const renderAccept = () => (
    <Box
      sx={{
        display: 'flex',
        pointerEvents: canBillingHoursEdit ? 'auto' : 'none',
      }}
    >
      <Box
        sx={{ cursor: 'pointer' }}
        display={'flex'}
        alignItems={'center'}
        onClick={handleApprove}
      >
        <AcceptIcon />
      </Box>
      <Box
        sx={{ cursor: 'pointer', marginLeft: '24px' }}
        display={'flex'}
        alignItems={'center'}
        onClick={handleReject}
      >
        <RejectIcon />
      </Box>
    </Box>
  );

  const handleUndo = useCallback(() => {
    if (context?.updateVisitStatus && appointment.visitId) {
      context.updateVisitStatus(
        activitySheetId,
        appointment.visitId,
        'Submitted',
        true,
      );
    }
  }, [context, activitySheetId, appointment.visitId]);

  const renderOnHold = () => (
    <Box sx={{ display: 'flex' }} alignItems={'center'}>
      <TextWithIcon
        LeftIcon={<StatusIcon fill={getStatusColor('On-hold')} />}
        labelId={'onHoldText'}
        defaultLabel="On-hold"
        className="paddingZero archivedIconMinusRemoveSm"
        sxProps={{
          color: getStatusColor('On-hold'),
          paddingRight: '4px',
          textTransform: 'none',
        }}
      />
    </Box>
  );

  const renderUndo = (status: StatusType | null) => {
    const showUndoButton = appointment.originalStatus !== appointment.status;

    return (
      <Box sx={{ display: 'flex' }} alignItems={'center'}>
        <TextWithIcon
          LeftIcon={<StatusIcon fill={getStatusColor(status)} />}
          label={status ?? ''}
          className="paddingZero archivedIconMinusRemoveSm"
          defaultLabel="Status"
          sxProps={{
            color: getStatusColor(status),
            paddingRight: '4px',
            textTransform: 'none',
          }}
        />
        {showUndoButton && !appointment.onHold && (
          <Box sx={{ cursor: 'pointer' }} onClick={handleUndo}>
            <UndoIcon />
          </Box>
        )}
      </Box>
    );
  };

  const getServiceCode = useCallback(
    (id: string | null) => {
      if (id === null) return '-';
      const code = serviceCode.find(
        (item) => item.code === id || item.id === id,
      );
      return code ? code.code : '-';
    },
    [serviceCode],
  );

  const getSettingsCode = useCallback(
    (id: string | null) => {
      if (id === null) return '-';
      const code = settingCode.find(
        (item) => item.code === id || item.id === id,
      );
      return code ? code.code : '-';
    },
    [settingCode],
  );

  const getClientLocation = useCallback(
    (clientLocation: string | null) => {
      if (clientLocation === null || clientLocation.length === 0) return '-';
      const code = clientLocations.find((item) => item.code === clientLocation);
      return code ? code.description : '-';
    },
    [clientLocations],
  );

  const getActivitySheetBackgroundColor = useCallback(
    (onHold: boolean | null) => {
      if (onHold) return '#D5D5D5';
      if (parentArchived) return 'var(--thom-mono-gray)';
      if (rowId === 0) return 'transparent';
      return 'transparent';
    },
    [parentArchived, rowId],
  );

  const getFormatedDurationInDecimal = useCallback(
    (duration: number | null) =>
      `${formatNumberToDecimal(duration) || '0.00'} hrs`,
    [],
  );

  const getCellClassName = useCallback(
    () => (removeBorder ? 'border-bottom-0' : 'enableBorderBottom'),
    [removeBorder],
  );

  const getFirstCellClassName = useCallback(() => {
    return removeBorder && !appointment.isLastIndex && isGroupBilling
      ? 'border-bottom-0'
      : 'enableBorderBottom';
  }, [removeBorder, appointment.isLastIndex, isGroupBilling]);

  const clientFullName = useMemo(
    () =>
      formatName(
        appointment.clientFirstName,
        appointment.clientMiddleName,
        appointment.clientLastName,
        appointment.clientSuffix ?? '',
      ),
    [
      appointment.clientFirstName,
      appointment.clientMiddleName,
      appointment.clientLastName,
      appointment.clientSuffix,
    ],
  );

  const showGroupBillingHeader =
    index === 0 && isGroupBilling && !hasInterpreter;
  const cellClassName = getCellClassName();

  return (
    <>
      {showGroupBillingHeader && (
        <GroupBillingHeader appointment={appointment} rctl={rctl} />
      )}
      <TableRow
        key={appointment.appointmentId}
        sx={{
          backgroundColor: getActivitySheetBackgroundColor(appointment.onHold),
        }}
      >
        <TableCell
          sx={{
            padding: '0px 14px !important',
          }}
          className={getFirstCellClassName()}
        >
          <Box
            sx={{
              backgroundColor: getActivitySheetBackgroundColor(null),
              borderLeft: appointment.isLastIndex ? '' : '1px solid #00C6B8',
              position: 'relative',
              '&::before': {
                display: appointment.isLastIndex ? 'block' : 'none',
                content: '""',
                position: 'absolute',
                left: -0.5,
                top: '0%', // Start 25% from the top to center the border vertically
                height: '50%', // 50% height of the cell
                width: '1px', // Border thickness
                backgroundColor: '#00C6B8', // Border color
              },
              height: '100%',
              alignContent: 'center',
              marginLeft: '16px',
            }}
          >
            {!isGroupBilling && (
              <Box display={'flex'} alignItems={'center'}>
                <Box
                  sx={{
                    width: '20px',
                    backgroundColor: '#00C6B8',
                    height: '1px',
                  }}
                />
                <SingleIcon />
              </Box>
            )}
          </Box>
        </TableCell>
        <TableCell className={cellClassName}>
          <Tooltip
            id="tool-tip"
            title={clientFullName.length > 22 ? clientFullName : ''}
            placement="bottom"
          >
            <Box>
              <MediumTypography
                label={clientFullName}
                sxProps={{
                  color: '#008C82',
                  maxWidth: '150px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              />
            </Box>
          </Tooltip>
        </TableCell>
        <TableCell padding={'none'} className={cellClassName}>
          <MediumTypography
            label={getAppStartTime(
              appointment.startTime,
              LOCAL_TIME_12_HOUR_FORMAT,
              true,
            )}
            sxProps={{ color: '#008C82', marginRight: '4px' }}
          />
        </TableCell>
        <TableCell padding={'none'} className={cellClassName}>
          <MediumTypography
            label={
              appointment.attendeeRole !== 'CLINICIAN'
                ? '-'
                : getFormatedDurationInDecimal(appointment.sessionDurationDec)
            }
          />
        </TableCell>
        <TableCell padding={'none'} className={cellClassName}>
          <MediumTypography label={getServiceCode(appointment.serviceCode)} />
        </TableCell>
        <TableCell padding={'none'} className={cellClassName}>
          <MediumTypography label={getSettingsCode(appointment.settingCode)} />
        </TableCell>
        <TableCell padding={'none'} className={cellClassName}>
          <MediumTypography
            label={getClientLocation(appointment.clientLocationType)}
          />
        </TableCell>
        <TableCell padding={'none'} className={cellClassName}>
          <MediumTypography label={appointment.modifier ?? '-'} />
        </TableCell>
        <TableCell
          className={isGroupBilling && removeBorder ? 'border-bottom-0' : ''}
        >
          {appointment.onHold && renderOnHold()}
          {!appointment.onHold &&
            appointment.status === 'Submitted' &&
            renderAccept()}
          {!appointment.onHold &&
            appointment.status &&
            appointment.status !== 'Submitted' &&
            renderUndo(appointment.status)}
          {!appointment.onHold && !appointment.status && (
            <TextWithIcon
              LeftIcon={<StatusIcon fill={getStatusColor('Not Submitted')} />}
              label="Not Submitted"
              className="paddingZero archivedIconMinusRemoveSm"
              defaultLabel="Status"
              sxProps={{
                color: getStatusColor('Not Submitted'),
                paddingRight: '4px',
                textTransform: 'none',
              }}
            />
          )}
        </TableCell>
        <VisitActionButtons
          appointment={appointment}
          removeBorder={removeBorder}
          parentStatus={parentStatus}
          selectedDate={selectedDate}
          activitySheetId={activitySheetId}
          activitySheetContext={context}
          onUpdateVisitNote={onUpdateVisitNote}
          navigate={navigate}
          rctl={rctl}
        />
      </TableRow>
    </>
  );
};

export default ActivitySheetVisitView;
